import React, { useState, useEffect } from 'react';
import './PropertyDetail.css';
import PropertyDocuments from './PropertyDocuments';
import ChatConversation from './ChatConversation';
import { supabase } from '../supabaseClient';
import { calcRetroMonths, isMonthBefore, buildRetroStartDate, defaultRetroMonth } from '../utils/expenseRetroactive';
import { calcRemainingPayments, calcLastPaymentLabel } from '../utils/expenseDuration';
import ExpenseSummary from './ExpenseSummary';
import MonthNavigator from '../components/MonthNavigator';
import PaymentHistory from '../components/PaymentHistory';
import MonthlyHistoryCard, { monthIndex, getTenantStartIndex, earliestMonth } from '../components/MonthlyHistoryCard';

function isFutureMonth(year, month) {
  const now = new Date();
  return year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth());
}

function getExpensesForMonth(expenses, year, month) {
  return expenses.filter(e => {
    // Gastos únicos: aparecer solo en su mes exacto
    const isUnico = e.type === 'puntual' || e.type === 'unico' || e.frequency === 'unico';
    if (isUnico) {
      if (!e.start_date) return false;
      const d = new Date(e.start_date.substring(0, 10) + 'T12:00:00');
      return d.getFullYear() === year && d.getMonth() === month;
    }
    const dateStr = e.start_date
      ? (e.start_date.substring(0, 10) + 'T12:00:00')
      : (e.created_at ? (e.created_at.substring(0, 10) + 'T12:00:00') : '');
    const start = new Date(dateStr);
    if (isNaN(start.getTime())) return false;
    const sy = start.getFullYear(), sm = start.getMonth();
    if (year < sy || (year === sy && month < sm)) return false;
    if (e.frequency === 'manual') return true;
    const monthsDiff = (year - sy) * 12 + (month - sm);
    const step = getFrequencyStep(e);
    if (e.type === 'recurrente_temporal') {
      if (monthsDiff >= (e.duration_payments || 0) * step) return false;
    }
    if (e.skipped_months && Array.isArray(e.skipped_months)) {
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      if (e.skipped_months.includes(monthStr)) return false;
    }
    return true;
  });
}

function getMonthlyEquivalent(expense) {
  const amt = Number(expense.amount) || 0;
  if (expense.frequency === 'trimestral') return amt / 3;
  if (expense.frequency === 'anual') return amt / 12;
  if (expense.frequency === 'custom') return amt / (expense.custom_frequency_months || 1);
  return amt;
}

function getExpensesForMonthReal(expenses, year, month) {
  return expenses.filter(e => {
    const isUnico = e.type === 'puntual' || e.type === 'unico' || e.frequency === 'unico';
    if (isUnico) {
      if (!e.start_date) return false;
      const d = new Date(e.start_date.substring(0, 10) + 'T12:00:00');
      return d.getFullYear() === year && d.getMonth() === month;
    }
    const dateStr = e.start_date
      ? (e.start_date.substring(0, 10) + 'T12:00:00')
      : (e.created_at ? (e.created_at.substring(0, 10) + 'T12:00:00') : '');
    const start = new Date(dateStr);
    if (isNaN(start.getTime())) return false;
    const sy = start.getFullYear(), sm = start.getMonth();
    if (year < sy || (year === sy && month < sm)) return false;
    if (e.frequency === 'manual') return true;
    const monthsDiff = (year - sy) * 12 + (month - sm);
    const step = getFrequencyStep(e);
    if (monthsDiff % step !== 0) return false;
    if (e.type === 'recurrente_temporal') {
      const paymentIndex = monthsDiff / step;
      if (paymentIndex >= (e.duration_payments || 0)) return false;
    }
    if (e.skipped_months && Array.isArray(e.skipped_months)) {
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      if (e.skipped_months.includes(monthStr)) return false;
    }
    return true;
  });
}

function getAmountForView(expense, view) {
  if (view === 'real') return Number(expense.amount) || 0;
  return getMonthlyEquivalent(expense);
}

function getFrequencyStep(expense) {
  if (expense.frequency === 'trimestral') return 3;
  if (expense.frequency === 'anual') return 12;
  if (expense.frequency === 'custom') return expense.custom_frequency_months || 1;
  return 1;
}

function getCurrentPeriodStart(expense, year, month) {
  if (!expense.start_date) return null;
  const start = new Date(expense.start_date.substring(0, 10) + 'T12:00:00');
  const sy = start.getFullYear(), sm = start.getMonth();
  const step = getFrequencyStep(expense);
  const monthsDiff = (year - sy) * 12 + (month - sm);
  const periodIndex = Math.floor(monthsDiff / step);
  const totalStartMonths = sy * 12 + sm + periodIndex * step;
  const pYear = Math.floor(totalStartMonths / 12);
  const pMonth = totalStartMonths % 12;
  return `${pYear}-${String(pMonth + 1).padStart(2, '0')}-01`;
}

function isExpensePendingVariable(expense, year, month) {
  if (expense.type !== 'recurrente_variable') return false;
  if (expense.frequency === 'manual') return false;
  const currentPeriodStart = getCurrentPeriodStart(expense, year, month);
  if (!currentPeriodStart) return false;
  const savedPeriodStart = expense.period_start
    ? expense.period_start.substring(0, 10)
    : null;
  return savedPeriodStart !== currentPeriodStart;
}

function isPaymentMonth(expense, year, month) {
  if (!expense.start_date) return true;
  const start = new Date(expense.start_date.substring(0, 10) + 'T12:00:00');
  const sy = start.getFullYear(), sm = start.getMonth();
  const step = getFrequencyStep(expense);
  const monthsDiff = (year - sy) * 12 + (month - sm);
  return monthsDiff % step === 0;
}

function AddTenantToRoomModal({ onClose, onAdd, room }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [startDay, setStartDay] = useState(room?.paymentConfig?.startDay?.toString() || '1');
  const [endDay, setEndDay] = useState(room?.paymentConfig?.endDay?.toString() || '5');

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd({ name, phone }, { startDay: parseInt(startDay), endDay: parseInt(endDay), limitDay: parseInt(endDay) });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Asignar inquilino</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre completo</label>
            <input type="text" placeholder="Ej: Laura Martínez" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ margin: 0 }}>Teléfono</label>
              <span style={{ fontSize: '11px', color: '#aaa', background: '#ebebeb', borderRadius: '6px', padding: '2px 8px', fontWeight: 500 }}>Opcional</span>
            </div>
            <input type="tel" placeholder="622 280 559" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Rango de días para pagar el alquiler</label>
            <div className="payment-range-inputs">
              <div className="payment-day-input">
                <label>Del día</label>
                <input type="number" min="1" max="28" value={startDay} onChange={(e) => setStartDay(e.target.value)} required />
              </div>
              <span className="range-separator">—</span>
              <div className="payment-day-input">
                <label>Al día</label>
                <input type="number" min="1" max="31" value={endDay} onChange={(e) => setEndDay(e.target.value)} required />
              </div>
            </div>
            <p className="payment-range-note">El inquilino deberá marcar el pago entre el día {startDay} y {endDay} de cada mes</p>
          </div>
          <button type="submit" className="submit-button">Asignar inquilino</button>
        </form>
      </div>
    </div>
  );
}

function generateTenantCode() {
  const digits = Math.floor(1000 + Math.random() * 9000).toString();
  const letters = String.fromCharCode(
    65 + Math.floor(Math.random() * 26),
    65 + Math.floor(Math.random() * 26)
  );
  return digits + letters;
}

function saveTenantCode(code, landlordEmail, propertyId, tenantId, roomId) {
  const codes = JSON.parse(localStorage.getItem('tenant_codes') || '{}');
  codes[code] = { landlordEmail, propertyId, tenantId, roomId };
  localStorage.setItem('tenant_codes', JSON.stringify(codes));
}

function ExpenseMenu({ expenseId, openMenuId, setOpenMenuId, onEdit, onDelete }) {
  const isOpen = openMenuId === expenseId;
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e) => {
      if (!menuRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen, setOpenMenuId]);

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpenMenuId(isOpen ? null : expenseId); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: '#aaa', lineHeight: 1, display: 'flex', alignItems: 'center' }}
        aria-label="Opciones del gasto"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          style={{
            position: 'absolute', right: 0, top: '100%', zIndex: 200,
            background: 'white', borderRadius: 10,
            boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
            border: '1px solid #eee', minWidth: 120, overflow: 'hidden',
            maxWidth: 'calc(100vw - 16px)',
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', color: '#333' }}
          >
            Editar
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onDelete(); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', color: '#F44336' }}
          >
            Eliminar
          </button>
        </div>
      )}
    </div>
  );
}

function RoomDetail({ room, property, onBack, onUpdate, landlordEmail }) {
  const [expenses, setExpenses] = useState([]);
  const [showExpenses, setShowExpenses] = useState(false);
  const [expenseView, setExpenseView] = useState('prorrateado');
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [openMenuExpenseId, setOpenMenuExpenseId] = useState(null);
  const [showExpenseSummary, setShowExpenseSummary] = useState(false);
  const [showEditRoom, setShowEditRoom] = useState(false);
  const [showEditTenant, setShowEditTenant] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const optionsMenuRef = React.useRef(null);
  React.useEffect(() => {
    if (!showOptionsMenu) return;
    const handleOutside = (e) => {
      if (!optionsMenuRef.current?.contains(e.target)) {
        setShowOptionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showOptionsMenu]);
  const [codeModal, setCodeModal] = useState(null); // code string when open
  const [copied, setCopied] = useState(false);
  const copyTimerRef = React.useRef(null);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPaymentHistoryPage, setShowPaymentHistoryPage] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [supabaseRoomPayment, setSupabaseRoomPayment] = useState(null);
  const [confirmModalAmount, setConfirmModalAmount] = useState(null);

  const handleCopyCode = (code) => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setCopied(true);
    clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleShareCode = (code) => {
    const shareText = `Hola, tu código de acceso para Domio es: ${code}. Entra en trydomio.com para gestionar tu alquiler.`;
    if (navigator.share) {
      navigator.share({ title: 'Código de acceso Domio', text: shareText, url: 'https://trydomio.com' }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(shareText).catch(() => {});
    }
  };

  useEffect(() => {
    if (!room.tenant || !landlordEmail) return;
    supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('landlord_email', landlordEmail)
      .eq('property_id', property.id)
      .eq('room_id', room.id)
      .eq('sender', 'tenant')
      .eq('read_by_landlord', false)
      .then(({ count }) => setUnreadCount(count || 0));
  }, [room.id, room.tenant, property.id, landlordEmail]); // eslint-disable-line

  useEffect(() => {
    supabase
      .from('expenses')
      .select('*')
      .eq('property_id', String(property.id))
      .eq('room_id', String(room.id))
      .order('created_at', { ascending: false })
      .then(({ data }) => { if (data) setExpenses(data); });
  }, [property.id, room.id]);

  const now = new Date();
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());

  const minYear = 2020;
  const minMonth = 0;

  const isCurrentMonthFuture = isFutureMonth(currentYear, currentMonth);
  const isAtMinMonth = currentYear === minYear && currentMonth === minMonth;
  const isAtCurrentMonth = currentYear === now.getFullYear() && currentMonth === now.getMonth();
  const isPastMonth = !isAtCurrentMonth && !isCurrentMonthFuture;
  const [isEditMode, setIsEditMode] = useState(false);
  const canEdit = isAtCurrentMonth || isEditMode;

  const goToPrevMonth = () => {
    if (isAtMinMonth) return;
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else { setCurrentMonth(m => m - 1); }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else { setCurrentMonth(m => m + 1); }
  };

  useEffect(() => {
    setIsEditMode(false);
  }, [currentYear, currentMonth]);

  useEffect(() => {
    let cancelled = false;
    const fetchPayment = () => {
      supabase
        .from('payments')
        .select('tenant_id, room_id, status, amount, confirmed_at, partial_amount, pending_amount')
        .eq('property_id', String(property.id))
        .eq('room_id', String(room.id))
        .eq('year', currentYear)
        .eq('month', currentMonth)
        .maybeSingle()
        .then(({ data }) => { if (!cancelled) setSupabaseRoomPayment(data || null); });
    };
    setSupabaseRoomPayment(null);
    fetchPayment();
    const pollInterval = setInterval(fetchPayment, 30000);
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchPayment(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentYear, currentMonth, property.id, room.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleExpenses = (expenseView === 'real'
    ? getExpensesForMonthReal(expenses, currentYear, currentMonth)
    : getExpensesForMonth(expenses, currentYear, currentMonth)
  ).filter(e => e.active !== false);
  const totalExpenses = visibleExpenses.reduce((sum, e) => {
    if (e.active === false) return sum;
    if (expenseView !== 'real' && isExpensePendingVariable(e, currentYear, currentMonth)) return sum;
    const pct = e.expense_percentage != null ? e.expense_percentage : (property.ownershipPercentage || 100);
    return sum + getAmountForView(e, expenseView) * pct / 100;
  }, 0);

  const payments = property.payments || [];

  // Todos los pagos confirmados de esta habitación en este mes (pueden ser varios si hubo cambio de inquilino)
  const confirmedPayments = payments.filter(p =>
    p.year === currentYear &&
    p.month === currentMonth &&
    p.roomId === room.id &&
    p.status === 'confirmed'
  );
  const currentPaymentConfirmed = confirmedPayments[confirmedPayments.length - 1]; // el más reciente para mostrar en card
  const currentPayment = payments.find(p =>
    p.year === currentYear &&
    p.month === currentMonth &&
    p.roomId === room.id
  );

  const ownershipPercentage = property.ownershipPercentage || 100;
  const ownershipMultiplier = ownershipPercentage / 100;
  // Suma de ingresos del mes: parcial usa supabaseRoomPayment, confirmado usa property.payments
  const monthlyIncome = supabaseRoomPayment?.status === 'partial'
    ? (supabaseRoomPayment.partial_amount || 0) * ownershipMultiplier
    : confirmedPayments.reduce((sum, p) => sum + (p.amount ?? room.price), 0) * ownershipMultiplier;
  const netIncome = isCurrentMonthFuture ? null : monthlyIncome - totalExpenses;

  const handleAddExpense = async (expenseData, prevStartDates) => {
    const base = { property_id: String(property.id), room_id: String(room.id), landlord_email: landlordEmail, ...expenseData };
    const rows = (prevStartDates && prevStartDates.length > 0)
      ? [base, ...prevStartDates.map(sd => ({ ...base, start_date: sd }))]
      : [base];
    const { data, error } = await supabase.from('expenses').insert(rows).select();
    if (error) { alert(`Error guardando el gasto: ${error.message}`); return; }
    setExpenses(prev => [...(data || []), ...prev]);
    setShowAddExpense(false);
  };

  const handleDeleteExpense = async (id) => {
    await supabase.from('expenses').delete().eq('id', id);
    setExpenses(prev => prev.filter(e => e.id !== id));
  };

  const handleUpdateExpense = async (expenseId, expenseData) => {
    const { data, error } = await supabase
      .from('expenses')
      .update(expenseData)
      .eq('id', expenseId)
      .select()
      .single();
    if (error) { alert(`Error actualizando el gasto: ${error.message}`); return; }
    setExpenses(prev => prev.map(e => e.id === expenseId ? data : e));
    setEditingExpense(null);
  };

  const handleUpdateVariableAmount = async (id, newAmount) => {
    const expense = expenses.find(e => e.id === id);
    const periodStart = getCurrentPeriodStart(expense, currentYear, currentMonth);
    await supabase.from('expenses').update({
      amount: parseFloat(newAmount),
      period_start: periodStart,
    }).eq('id', id);
    setExpenses(prev => prev.map(e =>
      e.id === id
        ? { ...e, amount: parseFloat(newAmount), period_start: periodStart }
        : e
    ));
  };

  const handleSaveRoom = (updatedData) => {
    const updatedRoom = { ...room, ...updatedData };
    const updatedRooms = property.rooms.map(r => r.id === room.id ? updatedRoom : r);
    onUpdate({ ...property, rooms: updatedRooms });
    setShowEditRoom(false);
  };

  const handleSaveTenant = (tenantData, paymentConfig) => {
    const updatedRoom = { ...room, tenant: { ...room.tenant, ...tenantData }, paymentConfig };
    const updatedRooms = property.rooms.map(r => r.id === room.id ? updatedRoom : r);
    onUpdate({ ...property, rooms: updatedRooms });
    setShowEditTenant(false);
  };

  const handleAddTenant = (tenantData, paymentConfig) => {
    const code = generateTenantCode();
    const tenantId = Date.now().toString();
    const updatedRoom = {
      ...room,
      tenant: { ...tenantData, id: tenantId, tenantCode: code },
      status: 'ocupada',
      paymentConfig
    };
    if (landlordEmail) {
      saveTenantCode(code, landlordEmail, property.id, tenantId, room.id);
    }
    const updatedRooms = property.rooms.map(r => r.id === room.id ? updatedRoom : r);
    onUpdate({ ...property, rooms: updatedRooms });
    setShowAddTenant(false);
  };

  const handleRemoveTenant = () => {
    if (window.confirm('¿Eliminar inquilino de esta habitación?')) {
      // Conservar el nombre del inquilino en sus pagos antes de eliminarlo
      const updatedPayments = (property.payments || []).map(p =>
        p.roomId === room.id && !p.tenantName && room.tenant
          ? { ...p, tenantName: room.tenant.name }
          : p
      );
      const updatedRoom = { ...room, tenant: null, status: 'vacia' };
      const updatedRooms = property.rooms.map(r => r.id === room.id ? updatedRoom : r);
      onUpdate({ ...property, rooms: updatedRooms, payments: updatedPayments });
    }
  };

  const handleRejectPayment = async () => {
    if (supabaseRoomPayment?.status === 'pending') {
      const { error: deleteError } = await supabase
        .from('payments')
        .delete()
        .eq('property_id', String(property.id))
        .eq('room_id', String(room.id))
        .eq('year', currentYear)
        .eq('month', currentMonth);

      if (deleteError) {
        alert('Error al rechazar el pago. Por favor, recarga la página.');
        return;
      }
      setSupabaseRoomPayment(null);
    }

    const updatedPayments = payments.filter(p =>
      !(p.year === currentYear && p.month === currentMonth && p.roomId === room.id)
    );
    onUpdate({ ...property, payments: updatedPayments });
  };

  // Abre el modal de confirmar pago con importe editable
  const handleMarkAsPending = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmWithAmount = async (importeNuevo) => {
    const previousState = supabaseRoomPayment;
    const previousPayments = payments;
    const importePrevio = supabaseRoomPayment?.partial_amount || 0;
    const nuevoTotal = importePrevio + importeNuevo;
    const isComplete = nuevoTotal >= room.price;
    const confirmedAt = new Date().toISOString();
    const newStatus = isComplete ? 'confirmed' : 'partial';
    const newPartialAmount = isComplete ? null : nuevoTotal;

    // Optimistic update
    setSupabaseRoomPayment(prev => ({
      ...(prev || {}),
      status: newStatus,
      partial_amount: newPartialAmount,
      pending_amount: null,
      amount: room.price,
      confirmed_at: confirmedAt,
    }));

    if (isComplete) {
      const newPayment = {
        year: currentYear,
        month: currentMonth,
        roomId: room.id,
        status: 'confirmed',
        markedAt: confirmedAt,
        confirmedAt,
        amount: room.price,
        tenantName: room.tenant?.name,
      };
      onUpdate({ ...property, payments: [...payments, newPayment] });
    }

    setShowConfirmModal(false);
    setConfirmModalAmount(null);

    const { data: rpcResult, error: rpcError } = await supabase.rpc('confirm_payment_v2', {
      p_property_id:      String(property.id),
      p_room_id:          String(room.id),
      p_tenant_id:        room.tenant?.id ? String(room.tenant.id) : null,
      p_year:             currentYear,
      p_month:            currentMonth,
      p_confirmed_amount: importeNuevo,
      p_rent_total:       room.price,
      p_landlord_email:   property.landlord_email || landlordEmail,
    });

    if (rpcError || rpcResult?.success === false) {
      console.error('[RoomDetail] Error guardando pago:', rpcError || rpcResult?.error);
      setSupabaseRoomPayment(previousState);
      if (isComplete) onUpdate({ ...property, payments: previousPayments });
      alert('Error al guardar el pago. Por favor, recarga la página.');
      return;
    }

    if (rpcResult) {
      setSupabaseRoomPayment(prev => ({
        ...(prev || {}),
        status: rpcResult.status,
        partial_amount: rpcResult.partial_amount,
        amount: rpcResult.amount,
        pending_amount: null,
      }));
    }
  };

  // Mark several past months as fully paid at once (from Historial)
  const handleBulkConfirm = async (tenantId, months, existingRows) => {
    const rentTotal = room.price || 0;
    const confirmedAt = new Date().toISOString();
    const results = await Promise.all(months.map(async ({ year, month }) => {
      const existing = existingRows.find(r => r.year === year && r.month === month);
      const remaining = rentTotal - (existing?.partial_amount || 0);
      const { data, error } = await supabase.rpc('confirm_payment_v2', {
        p_property_id:      String(property.id),
        p_room_id:          String(room.id),
        p_tenant_id:        tenantId ? String(tenantId) : null,
        p_year:             year,
        p_month:            month,
        p_confirmed_amount: remaining,
        p_rent_total:       rentTotal,
        p_landlord_email:   property.landlord_email || landlordEmail,
      });
      return { year, month, ok: !error && data?.success !== false };
    }));

    const saved = results.filter(r => r.ok);
    if (saved.length > 0) {
      const updatedPayments = [...payments];
      saved.forEach(({ year, month }) => {
        const idx = updatedPayments.findIndex(p => p.year === year && p.month === month && p.roomId === room.id);
        const entry = { year, month, roomId: room.id, status: 'confirmed', confirmedAt, amount: rentTotal, tenantName: room.tenant?.name };
        if (idx >= 0) updatedPayments[idx] = { ...updatedPayments[idx], ...entry };
        else updatedPayments.push({ ...entry, markedAt: confirmedAt });
      });
      onUpdate({ ...property, payments: updatedPayments });
      if (saved.some(r => r.year === currentYear && r.month === currentMonth)) {
        setSupabaseRoomPayment(prev => ({
          ...(prev || {}), status: 'confirmed', partial_amount: null, pending_amount: null, amount: rentTotal, confirmed_at: confirmedAt,
        }));
      }
    }
    return { saved: saved.length, failed: results.length - saved.length };
  };

  const handleConfirmPartialRest = () => {
    const restante = Math.max(0, room.price - (supabaseRoomPayment?.partial_amount || 0));
    setConfirmModalAmount(restante);
    setShowConfirmModal(true);
  };

  if (showPaymentHistoryPage) {
    return (
      <PaymentHistory
        propertyId={property.id}
        roomId={room.id}
        payments={payments}
        tenants={room.tenant ? [{ id: room.tenant.id || null, name: room.tenant.name, amount: room.price }] : []}
        onBulkConfirm={handleBulkConfirm}
        onSyncPayments={(updated) => onUpdate({ ...property, payments: updated })}
        onBack={() => setShowPaymentHistoryPage(false)}
      />
    );
  }

  if (showDocuments) {
    return <PropertyDocuments property={property} room={room} landlordEmail={landlordEmail} onBack={() => setShowDocuments(false)} onUpdate={onUpdate} />;
  }

  if (showExpenseSummary) {
    const getMonthlyIncome = (yr, mo) =>
      (property.payments || [])
        .filter(p => p.year === yr && p.month === mo && p.roomId === room.id && p.status === 'confirmed')
        .reduce((s, p) => s + (p.amount ?? room.price), 0) * ((property.ownershipPercentage || 100) / 100);
    return (
      <ExpenseSummary
        expenses={expenses}
        onBack={() => setShowExpenseSummary(false)}
        propertyName={`${property.name} — ${room.name}`}
        getMonthlyIncome={getMonthlyIncome}
      />
    );
  }

  if (showChat && room.tenant) {
    return (
      <ChatConversation
        landlordEmail={landlordEmail}
        propertyId={property.id}
        roomId={room.id}
        tenantId={room.tenant.id || room.id}
        tenantName={room.tenant.name}
        propertyName={`${property.name} — ${room.name}`}
        currentRole="landlord"
        onBack={() => setShowChat(false)}
      />
    );
  }

  return (
    <div className="property-detail-container">
      {/* Header */}
      <div className="detail-header" ref={optionsMenuRef}>
        <button className="back-button" onClick={onBack}>←</button>
        <h1 className="detail-title">{room.name}</h1>
        <button className="detail-options" onClick={() => setShowOptionsMenu(!showOptionsMenu)}>⋮</button>
        {showOptionsMenu && (
          <div className="detail-options-menu">
            <button className="detail-option-item" onClick={() => { setShowEditRoom(true); setShowOptionsMenu(false); }}>
              Editar
            </button>
            {room.tenant && (
              <button className="detail-option-item" onClick={() => {
                let code = room.tenant.tenantCode;
                if (!code) {
                  code = generateTenantCode();
                  const updatedRoom = { ...room, tenant: { ...room.tenant, tenantCode: code } };
                  const updatedRooms = property.rooms.map(r => r.id === room.id ? updatedRoom : r);
                  onUpdate({ ...property, rooms: updatedRooms });
                  if (landlordEmail) saveTenantCode(code, landlordEmail, property.id, room.tenant.id, room.id);
                }
                setCodeModal(code);
                setShowOptionsMenu(false);
              }}>
                Código del inquilino
              </button>
            )}
            <button className="detail-option-item delete" onClick={() => {
              if (window.confirm('¿Estás seguro de que quieres eliminar esta habitación?')) {
                const updatedRooms = property.rooms.filter(r => r.id !== room.id);
                onUpdate({ ...property, rooms: updatedRooms });
                onBack();
              }
              setShowOptionsMenu(false);
            }}>
              Eliminar
            </button>
          </div>
        )}
        {codeModal && (
          <div className="modal-overlay" onClick={() => { setCodeModal(null); setCopied(false); clearTimeout(copyTimerRef.current); }}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
              <div className="modal-header">
                <h2>Código del inquilino</h2>
                <button className="modal-close" onClick={() => { setCodeModal(null); setCopied(false); clearTimeout(copyTimerRef.current); }}>×</button>
              </div>
              <p style={{ color: '#888', fontSize: '14px', margin: '0 0 20px' }}>Comparte este código con <strong>{room.tenant?.name}</strong></p>
              <div style={{ background: '#f5f5f5', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <p style={{ fontSize: '36px', fontWeight: '700', letterSpacing: '8px', margin: 0, color: '#111' }}>{codeModal}</p>
              </div>
              <button className="submit-button" onClick={() => handleCopyCode(codeModal)}>
                {copied ? 'Copiado' : 'Copiar código'}
              </button>
              <button
                onClick={() => handleShareCode(codeModal)}
                style={{
                  marginTop: 10, width: '100%', padding: '12px', borderRadius: '12px',
                  background: 'white', border: '1px solid #111', cursor: 'pointer',
                  fontSize: '15px', fontWeight: '600', color: '#111',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="16 6 12 2 8 6" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="2" x2="12" y2="15" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Compartir
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Month Navigator */}
      <MonthNavigator
        year={currentYear}
        month={currentMonth}
        minYear={minYear}
        minMonth={minMonth}
        onPrev={goToPrevMonth}
        onNext={goToNextMonth}
        onJump={(yr, mo) => { setCurrentYear(yr); setCurrentMonth(mo); }}
        isPastMonth={isPastMonth}
        isEditMode={isEditMode}
        onEdit={() => setIsEditMode(true)}
        onSave={() => setIsEditMode(false)}
        onCancel={() => setIsEditMode(false)}
      />

      {/* Gauge */}
      <div className="profitability-section">
        <div className="gauge-chart">
          <svg width="360" height="220" viewBox="0 0 360 220">
            <path d="M 50 180 A 130 130 0 0 1 310 180" fill="none" stroke="#E0E0E0" strokeWidth="35" strokeLinecap="round"/>
            {!isCurrentMonthFuture && netIncome !== null && (
              netIncome >= 0 ? (
                <path d="M 50 180 A 130 130 0 0 1 310 180" fill="none" stroke="#4CAF50" strokeWidth="35" strokeLinecap="round"
                  strokeDasharray="408" strokeDashoffset={408 - (Math.min(netIncome / room.price, 1) * 408)}/>
              ) : (
                <path d="M 50 180 A 130 130 0 0 1 310 180" fill="none" stroke="#F44336" strokeWidth="35" strokeLinecap="round"/>
              )
            )}
          </svg>
          <div className="gauge-center">
            {isCurrentMonthFuture ? (
              <><div className="net-income future">—</div><div className="income-sublabel">Mes futuro</div></>
            ) : (
              <>
                <div className="net-income">{netIncome >= 0 ? '+' : ''}{netIncome.toFixed(2)} €</div>
                <div className="income-label">/mes</div>
                <div className="income-sublabel" style={{ color: netIncome < 0 ? '#F44336' : '#999' }}>
                  {netIncome < 0 && !currentPaymentConfirmed ? 'Pago sin confirmar' : 'Neto'}
                </div>
              </>
            )}
          </div>
        </div>
        <p className="profitability-label">{isCurrentMonthFuture ? 'Sin datos todavía' : 'Rentabilidad mensual estimada'}</p>
      </div>

      {/* Estado */}
      <div className="info-card status-card" onClick={() => setShowDocuments(true)} style={{ cursor: 'pointer' }}>
        <div className="card-header">
          <div>
            <h3>Estado</h3>
            <div className="status-info">
              <span className={`status-dot ${room.tenant ? 'alquilado' : 'vacio'}`}></span>
              <span>{room.tenant ? 'Alquilada' : 'Vacía'}</span>
            </div>
          </div>
          <button className="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Inquilino */}
      <div className="info-card tenant-card">
        <div className="card-header">
          <h3>Inquilino</h3>
        </div>
        {room.tenant ? (
          <div className="tenant-item" onClick={() => setShowEditTenant(true)} style={{ cursor: 'pointer', marginTop: '8px' }}>
            <div className="tenant-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="tenant-item-info">
              <p className="tenant-name">{room.tenant.name}</p>
              <p className="tenant-phone">{room.tenant.phone}</p>
            </div>
            <button className="tenant-chat-icon" onClick={(e) => { e.stopPropagation(); setShowChat(true); }}>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -4, right: -4,
                    background: '#e74c3c', color: 'white',
                    borderRadius: '50%', width: 16, height: 16,
                    fontSize: 10, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {unreadCount}
                  </span>
                )}
              </div>
            </button>
          </div>
        ) : (
          <button className="add-tenant-button" onClick={() => setShowAddTenant(true)}>
            + Añadir inquilino
          </button>
        )}
      </div>

      {/* Pago del alquiler */}
      {(room.tenant || confirmedPayments.length > 0 || currentPayment || canEdit) && (
        <div className="info-card payment-card">
          <div className="card-header">
            <h3>Pago del alquiler</h3>
          </div>

          <div className="payment-content">
            <div className="tenants-payment-grid">

              {/* Card del inquilino actual */}
              {room.tenant && (() => {
                const isPaid = supabaseRoomPayment
                  ? supabaseRoomPayment.status === 'confirmed'
                  : confirmedPayments.length > 0;
                const isPartial = supabaseRoomPayment?.status === 'partial';
                const isPendingConfirmation = supabaseRoomPayment?.status === 'pending';
                const isPending = !isPaid && !isPartial && !isPendingConfirmation &&
                  currentPayment && currentPayment.status !== 'confirmed';
                const badgeColor = isPaid ? 'green' : isPartial ? 'yellow' : (isPending || isPendingConfirmation) ? 'orange' : 'gray';
                const badgeIcon = isPaid ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17l-5-5" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : isPartial ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2a10 10 0 0 1 0 20V2z" fill="#F59E0B"/>
                    <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="2" fill="none"/>
                  </svg>
                ) : (isPending || isPendingConfirmation) ? (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="#FF9800" strokeWidth="2.5"/>
                  </svg>
                ) : (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="#9E9E9E" strokeWidth="2.5"/>
                  </svg>
                );

                return (
                  <div className="tenant-payment-card">
                    <div className="tenant-payment-header">
                      <div className="tenant-avatar">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="7" r="4" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="tenant-payment-info">
                        <p className="tenant-payment-amount" style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111' }}>{room.price}€</p>
                      </div>
                      <span className={`payment-status-badge ${badgeColor} small`}>{badgeIcon}</span>
                    </div>

                    {/* Pagado */}
                    {isPaid && confirmedPayments.map((p, i) => (
                      <div key={i} className="tenant-payment-actions">
                        <p className="payment-confirmed-text">
                          ✓ {p.amount !== undefined ? `${p.amount} €` : `${room.price} €`}
                          {p.amount !== undefined && p.amount !== room.price && (
                            <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '4px' }}>(base: {room.price} €)</span>
                          )}
                          {' · '}{new Date(p.confirmedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                        </p>
                        {canEdit && (
                          <button className="payment-cancel-link" onClick={async () => {
                            const { error } = await supabase
                              .from('payments')
                              .delete()
                              .eq('property_id', String(property.id))
                              .eq('room_id', String(room.id))
                              .eq('year', p.year)
                              .eq('month', p.month);
                            if (error) {
                              alert('Error al cancelar el pago. Por favor, recarga la página.');
                              return;
                            }
                            setSupabaseRoomPayment(null);
                            const updated = payments.filter(x => !(x.year === p.year && x.month === p.month && x.roomId === p.roomId && x.confirmedAt === p.confirmedAt));
                            onUpdate({ ...property, payments: updated });
                          }}>Cancelar</button>
                        )}
                      </div>
                    ))}

                    {/* Pago parcial */}
                    {isPartial && (
                      <div className="tenant-payment-actions">
                        <p className="payment-confirmed-text" style={{ color: '#92400E' }}>
                          {Number(supabaseRoomPayment.partial_amount).toFixed(2)}€ de {room.price}€ — pago parcial
                        </p>
                        {canEdit && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="payment-btn confirm small" onClick={handleConfirmPartialRest}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Confirmar resto
                            </button>
                            <button className="payment-cancel-link" onClick={async () => {
                              const { error } = await supabase
                                .from('payments')
                                .delete()
                                .eq('property_id', String(property.id))
                                .eq('room_id', String(room.id))
                                .eq('year', currentYear)
                                .eq('month', currentMonth);
                              if (error) {
                                alert('Error al cancelar el pago. Por favor, recarga la página.');
                                return;
                              }
                              setSupabaseRoomPayment(null);
                            }}>Cancelar</button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Pago enviado por el inquilino — pendiente de confirmar */}
                    {canEdit && isPendingConfirmation && (
                      <div className="tenant-payment-actions">
                        <p style={{ fontSize: '13px', color: '#92400E', margin: '0 0 8px 0' }}>
                          {supabaseRoomPayment?.pending_amount != null
                            ? `${Number(supabaseRoomPayment.pending_amount).toFixed(2)} € enviados — pendiente de confirmar`
                            : 'Pago enviado — pendiente de confirmar'}
                        </p>
                        <button className="payment-btn confirm small" onClick={() => { setConfirmModalAmount(supabaseRoomPayment?.pending_amount ?? null); setShowConfirmModal(true); }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Confirmar
                        </button>
                        <button className="payment-btn reject small" onClick={handleRejectPayment}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Rechazar
                        </button>
                      </div>
                    )}

                    {/* Pendiente de confirmar */}
                    {canEdit && isPending && (
                      <div className="tenant-payment-actions">
                        <button className="payment-btn confirm small" onClick={() => setShowConfirmModal(true)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Confirmar
                        </button>
                        <button className="payment-btn reject small" onClick={handleRejectPayment}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Rechazar
                        </button>
                      </div>
                    )}

                    {/* Sin pago */}
                    {canEdit && !isPaid && !isPartial && !isPending && !isPendingConfirmation && (
                      <div className="tenant-payment-actions">
                        <button className="payment-btn confirm small full-width" onClick={handleMarkAsPending}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Confirmar pago
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Sin inquilino — confirmar pago manualmente */}
              {!room.tenant && canEdit && confirmedPayments.length === 0 && !supabaseRoomPayment && (
                <div className="tenant-payment-actions">
                  <button className="payment-btn confirm small full-width" onClick={handleMarkAsPending}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Confirmar pago
                  </button>
                </div>
              )}

              {/* Sin inquilino — pago ya confirmado */}
              {!room.tenant && confirmedPayments.length > 0 && confirmedPayments.map((p, i) => (
                <div key={i} className="tenant-payment-card">
                  <div className="tenant-payment-header">
                    <div className="tenant-payment-info">
                      <p className="tenant-payment-amount" style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111' }}>{p.amount ?? room.price} €</p>
                    </div>
                    <span className="payment-status-badge green small">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6L9 17l-5-5" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </div>
                  <div className="tenant-payment-actions">
                    <p className="payment-confirmed-text">
                      ✓ {p.amount ?? room.price} € · {new Date(p.confirmedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                    </p>
                    {canEdit && (
                      <button className="payment-cancel-link" onClick={async () => {
                        const { error } = await supabase.from('payments').delete()
                          .eq('property_id', String(property.id)).eq('room_id', String(room.id))
                          .eq('year', p.year).eq('month', p.month);
                        if (error) { alert('Error al cancelar el pago.'); return; }
                        setSupabaseRoomPayment(null);
                        onUpdate({ ...property, payments: payments.filter(x => !(x.year === p.year && x.month === p.month && x.roomId === p.roomId && x.confirmedAt === p.confirmedAt)) });
                      }}>Cancelar</button>
                    )}
                  </div>
                </div>
              ))}

              {/* Rango de días de pago */}
              {(room.paymentConfig || property.paymentConfig) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px', paddingLeft: '2px' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="2" x2="16" y2="6" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="8" y1="2" x2="8" y2="6" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="3" y1="10" x2="21" y2="10" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span style={{ fontSize: '12px', color: '#aaa' }}>
                    {(() => { const pc = room.paymentConfig || property.paymentConfig; return `Rango de pago: ${pc.startDay}–${pc.endDay} ${new Date(currentYear, currentMonth).toLocaleDateString('es-ES', { month: 'short' })}`; })()}
                  </span>
                </div>
              )}

              {/* Pagos de ex-inquilinos */}
              {room.tenant && confirmedPayments
                .filter(p => p.tenantName !== room.tenant.name)
                .map((p, i) => (
                  <div key={i} className="tenant-payment-card" style={{ marginTop: '8px' }}>
                    <div className="tenant-payment-header">
                      <div className="tenant-avatar">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="7" r="4" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="tenant-payment-info">
                        <p className="tenant-payment-name" style={{ color: '#888' }}>{p.tenantName}</p>
                        <p className="tenant-payment-amount">{p.amount} €</p>
                      </div>
                      <span className="payment-status-badge green small">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17l-5-5" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </div>
                    <div className="tenant-payment-actions">
                      <p className="payment-confirmed-text">
                        ✓ {p.amount} € · {new Date(p.confirmedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                      </p>
                      {canEdit && (
                        <button className="payment-cancel-link" onClick={() => {
                          const updated = payments.filter(x => !(x.year === p.year && x.month === p.month && x.roomId === p.roomId && x.confirmedAt === p.confirmedAt));
                          onUpdate({ ...property, payments: updated });
                        }}>Cancelar</button>
                      )}
                    </div>
                  </div>
                ))
              }

              <button className="payment-history-btn" style={{ marginTop: 12 }} onClick={() => setShowPaymentHistoryPage(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <polyline points="23 4 23 10 17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Historial de pagos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gastos */}
      <div className="info-card expenses-card">
        <div className="card-header clickable" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }} onClick={() => setShowExpenses(!showExpenses)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0 }}>Gastos de esta habitación</h3>
            <span className="expenses-total" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{totalExpenses.toFixed(2)} {expenseView === 'real' ? '€' : '€/mes'}</span>
              <span className="arrow">{showExpenses ? '▼' : '›'}</span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
            <button type="button" onClick={e => { e.stopPropagation(); setShowExpenseSummary(true); }}
              style={{ fontSize: 11, padding: '3px 9px', borderRadius: 8, border: '1px solid #ddd', background: 'white', color: '#666', cursor: 'pointer' }}>
              Resumen
            </button>
            <div style={{ display: 'flex', background: '#f0f0f0', borderRadius: 6, padding: 2, gap: 1 }}>
              {['prorrateado', 'real'].map(v => (
                <button key={v} onClick={() => setExpenseView(v)} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, border: 'none', background: expenseView === v ? 'white' : 'transparent', color: expenseView === v ? '#333' : '#999', cursor: 'pointer', fontWeight: expenseView === v ? 600 : 400 }}>
                  {v === 'prorrateado' ? 'Prorrat.' : 'Real'}
                </button>
              ))}
            </div>
          </div>
        </div>
        {showExpenses && (
          <div className="expenses-detail">
            {visibleExpenses.length === 0 ? (
              <p className="no-expenses">No hay gastos añadidos</p>
            ) : visibleExpenses.map(exp => {
              const isPendingVariable = expenseView !== 'real' && isExpensePendingVariable(exp, currentYear, currentMonth);
              const monthly = isPendingVariable ? 0 : getAmountForView(exp, expenseView);
              const isPaying = expenseView === 'real' || isPaymentMonth(exp, currentYear, currentMonth);
              const showPendingInput = isPendingVariable && isPaymentMonth(exp, currentYear, currentMonth);
              const freqLabel = { trimestral: 'Trimestral', anual: 'Anual', unico: 'Único', manual: 'Manual', custom: `Cada ${exp.custom_frequency_months}m`, mensual: null }[exp.frequency] || null;
              const typeLabel = { recurrente_fijo: 'Fijo', recurrente_variable: 'Variable', recurrente_temporal: 'Temporal', puntual: 'Único' }[exp.type] || null;
              return (
                <div key={exp.id} className="expense-item" style={{ opacity: exp.active === false ? 0.5 : 1 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span>{exp.name}</span>
                      {exp.attachment_url && (
                        <a href={exp.attachment_url} target="_blank" rel="noreferrer" title="Ver adjunto" onClick={e => e.stopPropagation()} style={{ color: '#90CAF9', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                          </svg>
                        </a>
                      )}
                      {typeLabel && <span className={`frequency-badge ${exp.type}`} style={{ fontSize: 10 }}>{typeLabel}</span>}
                      {freqLabel && (
                        <span
                          className={`frequency-badge ${exp.frequency}`}
                          style={!isPaying ? { opacity: 0.4 } : undefined}
                        >{freqLabel}</span>
                      )}
                      {exp.type === 'recurrente_temporal' && exp.duration_payments && (
                        <span style={{ fontSize: 10, color: '#aaa' }}>({exp.payments_made || 0}/{exp.duration_payments} pagos)</span>
                      )}
                      {exp.active === false && <span style={{ fontSize: 10, color: '#aaa' }}>Pausado</span>}
                      {exp.expense_percentage != null && <span style={{ fontSize: 10, color: '#aaa' }}>{exp.expense_percentage}%</span>}
                    </div>
                    {showPendingInput && (
                      <PendingVariableInput expenseId={exp.id} onSave={handleUpdateVariableAmount} />
                    )}
                  </div>
                  <div className="expense-actions" style={{ gap: 6 }}>
                    <span style={{ color: isPendingVariable ? '#FFA726' : undefined }}>
                      {isPendingVariable ? '— €' : `${monthly.toFixed(2)} ${expenseView === 'real' ? '€' : '€/mes'}`}
                    </span>
                    {canEdit && (
                      <ExpenseMenu
                        expenseId={exp.id}
                        openMenuId={openMenuExpenseId}
                        setOpenMenuId={setOpenMenuExpenseId}
                        onEdit={() => { setEditingExpense(exp); setOpenMenuExpenseId(null); }}
                        onDelete={() => handleDeleteExpense(exp.id)}
                      />
                    )}
                  </div>
                </div>
              );
            })}
            {visibleExpenses.length > 0 && (
              <div className="expense-item total"><strong>{expenseView === 'real' ? 'Total mes' : 'Equiv. mensual'}</strong><strong>{totalExpenses.toFixed(2)} €</strong></div>
            )}
            {canEdit && <button className="add-expense-button" onClick={() => setShowAddExpense(true)}>+ Añadir gasto</button>}
          </div>
        )}
        {!showExpenses && canEdit && (
          <button className="add-expense-button" onClick={() => setShowAddExpense(true)}>+ Añadir gasto</button>
        )}
      </div>

      {/* Historial */}
      {(() => {
        const roomPayments = payments.filter(p => p.roomId === room.id);
        const ownershipMultiplier = (property.ownershipPercentage || 100) / 100;
        const tenantStartIdx = getTenantStartIndex(room.tenant);
        const historyStart = earliestMonth([
          property.createdAt,
          ...roomPayments.map(p => new Date(p.year, p.month, 1)),
          ...expenses.map(e => e.start_date ? e.start_date.substring(0, 10) + 'T12:00:00' : null),
          tenantStartIdx != null ? new Date(Math.floor(tenantStartIdx / 12), tenantStartIdx % 12, 1) : null,
        ]);
        const nowIdx = monthIndex(now.getFullYear(), now.getMonth());
        // No contract dates: occupied if there is a payment record that month, or the
        // current tenant was already added by then (unknown start → current month only)
        const isOccupied = (y, m) =>
          roomPayments.some(p => p.year === y && p.month === m) ||
          (!!room.tenant && (tenantStartIdx != null ? tenantStartIdx : nowIdx) <= monthIndex(y, m));
        const computeMonth = (y, m) => {
          const monthExpenses = expenseView === 'real'
            ? getExpensesForMonthReal(expenses, y, m)
            : getExpensesForMonth(expenses, y, m);
          const totalExp = monthExpenses.reduce((sum, e) => {
            if (e.active === false) return sum;
            if (expenseView !== 'real' && isExpensePendingVariable(e, y, m)) return sum;
            const pct = e.expense_percentage != null ? e.expense_percentage : (property.ownershipPercentage || 100);
            return sum + getAmountForView(e, expenseView) * pct / 100;
          }, 0);
          const confirmedPayment = roomPayments.find(p => p.year === y && p.month === m && p.status === 'confirmed');
          const income = confirmedPayment ? (confirmedPayment.amount ?? room.price) * ownershipMultiplier : 0;
          const status = isOccupied(y, m)
            ? { label: 'Alquilado', active: true }
            : { label: 'Vacío', active: false };
          return { income, expenses: totalExp, status };
        };
        return (
          <MonthlyHistoryCard
            name={room.name}
            exportTitle="Historial habitación"
            startYear={historyStart.year}
            startMonth={historyStart.month}
            computeMonth={computeMonth}
            onSelectMonth={(y, m) => { setCurrentYear(y); setCurrentMonth(m); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          />
        );
      })()}

      {/* Modals */}
      {showConfirmModal && (
        <ConfirmPaymentModal
          defaultAmount={confirmModalAmount !== null ? confirmModalAmount : room.price}
          tenantName={room.tenant?.name}
          onClose={() => { setShowConfirmModal(false); setConfirmModalAmount(null); }}
          onConfirm={handleConfirmWithAmount}
        />
      )}

      {showAddTenant && (
        <AddTenantToRoomModal
          room={room}
          onClose={() => setShowAddTenant(false)}
          onAdd={handleAddTenant}
        />
      )}

      {(showAddExpense || editingExpense) && (
        <AddExpenseModal
          onClose={() => { setShowAddExpense(false); setEditingExpense(null); }}
          onAdd={handleAddExpense}
          onUpdate={handleUpdateExpense}
          defaultExpensePct={property.ownershipPercentage || 100}
          initialExpense={editingExpense}
          landlordEmail={landlordEmail}
          propertyId={property.id}
          defaultDate={`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`}
          isPastMonth={isPastMonth}
        />
      )}

      {showEditRoom && (
        <EditRoomModal
          room={room}
          onClose={() => setShowEditRoom(false)}
          onSave={handleSaveRoom}
          onRemoveTenant={handleRemoveTenant}
        />
      )}

      {showEditTenant && room.tenant && (
        <EditTenantModal
          tenant={room.tenant}
          room={room}
          onClose={() => setShowEditTenant(false)}
          onSave={handleSaveTenant}
          onRemove={() => { handleRemoveTenant(); setShowEditTenant(false); }}
        />
      )}

    </div>
  );
}

function PendingVariableInput({ expenseId, onSave }) {
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} style={{ marginTop: 4, fontSize: 11, background: '#FFF3E0', border: '1px solid #FFB74D', borderRadius: 6, padding: '3px 8px', color: '#E65100', cursor: 'pointer' }}>
        ⚠ Introducir importe
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
      <input type="number" step="0.01" placeholder="0.00" value={value} onChange={e => setValue(e.target.value)}
        style={{ width: 80, fontSize: 13, padding: '3px 6px', borderRadius: 6, border: '1px solid #ddd' }} autoFocus />
      <button onClick={() => { if (value) { onSave(expenseId, value); setEditing(false); } }}
        style={{ fontSize: 11, background: '#4CAF50', color: 'white', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>
        Guardar
      </button>
      <button onClick={() => setEditing(false)} style={{ fontSize: 11, background: 'none', border: 'none', color: '#aaa', cursor: 'pointer' }}>×</button>
    </div>
  );
}

const EXPENSE_CATEGORIES = [
  { key: 'comunidad',    label: 'Comunidad',         subcategories: ['Cuota', 'Derrama', 'Otros'] },
  { key: 'suministros',  label: 'Suministros',        subcategories: ['Luz', 'Agua', 'Gas', 'Internet', 'Otros'] },
  { key: 'seguros',      label: 'Seguros',            subcategories: ['Hogar', 'Impago', 'Responsabilidad civil', 'Otros'] },
  { key: 'reparaciones', label: 'Reparaciones',       subcategories: ['Fontanería', 'Electricidad', 'Pintura', 'Electrodomésticos', 'Otros'] },
  { key: 'impuestos',    label: 'Impuestos',          subcategories: ['IBI', 'Basuras', 'Otros'] },
  { key: 'gestion',      label: 'Gestión',            subcategories: ['Agencia', 'Gestoría', 'Otros'] },
  { key: 'hipoteca',     label: 'Hipoteca/Préstamo',  subcategories: [] },
  { key: 'otros',        label: 'Otros',              subcategories: [] },
];

function AddExpenseModal({ onClose, onAdd, onUpdate, defaultExpensePct, initialExpense, landlordEmail, propertyId, defaultDate, isPastMonth, existingExpenses }) {
  const today = new Date().toISOString().split('T')[0];
  const isEditing = !!initialExpense;
  const [category, setCategory] = useState(isEditing ? (initialExpense.category || '') : '');
  const [subcategory, setSubcategory] = useState(isEditing ? (initialExpense.subcategory || '') : '');
  const [description, setDescription] = useState(isEditing ? (initialExpense.description || '') : '');
  const [amount, setAmount] = useState(isEditing ? (initialExpense.amount != null ? String(initialExpense.amount) : '') : '');
  const [repeats, setRepeats] = useState(isEditing ? initialExpense.frequency !== 'unico' : false);
  const [frequency, setFrequency] = useState(isEditing ? (initialExpense.frequency === 'unico' ? 'mensual' : initialExpense.frequency) : 'mensual');
  const [customFreqMonths, setCustomFreqMonths] = useState(isEditing ? (initialExpense.custom_frequency_months ? String(initialExpense.custom_frequency_months) : '') : '');
  const [durationType, setDurationType] = useState(isEditing ? (initialExpense.duration_payments ? 'pagos' : 'indefinido') : 'indefinido');
  const [durationPayments, setDurationPayments] = useState(isEditing ? (initialExpense.duration_payments ? String(initialExpense.duration_payments) : '') : '');
  const [firstPaymentDate, setFirstPaymentDate] = useState('');
  const [startDate, setStartDate] = useState(isEditing ? (initialExpense.start_date || today) : (defaultDate || today));
  const [expensePct, setExpensePct] = useState(isEditing ? (initialExpense.expense_percentage != null ? String(initialExpense.expense_percentage) : '') : (defaultExpensePct != null ? String(defaultExpensePct) : ''));
  const fileInputRef = React.useRef(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState(isEditing ? (initialExpense.attachment_url || '') : '');
  const [uploading, setUploading] = useState(false);
  const viewedYear = defaultDate ? parseInt(defaultDate.substring(0, 4)) : new Date().getFullYear();
  const viewedMonth = defaultDate ? parseInt(defaultDate.substring(5, 7)) - 1 : new Date().getMonth();
  const maxPrevYear = viewedMonth > 0 ? viewedYear : viewedYear - 1;
  const [selectedMonths, setSelectedMonths] = useState(() => new Set());
  const [prevPickerYear, setPrevPickerYear] = useState(maxPrevYear);
  const [applyToPrev, setApplyToPrev] = useState(false);
  const [applyToPrevRecurring, setApplyToPrevRecurring] = useState(false);
  const [retroYear, setRetroYear] = useState('');
  const [retroMonth, setRetroMonth] = useState('');

  const handleCategoryChange = (val) => { setCategory(val); setSubcategory(''); };

  const hasAmount = amount !== '' && parseFloat(amount) > 0;
  const derivedType = !repeats
    ? 'puntual'
    : hasAmount
      ? (durationType === 'pagos' ? 'recurrente_temporal' : 'recurrente_fijo')
      : 'recurrente_variable';

  const hasExistingRecurring = !isEditing && repeats && category
    ? (existingExpenses || []).some(e =>
        e.active !== false &&
        e.category === category &&
        e.frequency === frequency &&
        (frequency !== 'custom' || (e.custom_frequency_months || 1) === (parseInt(customFreqMonths) || 1))
      )
    : false;
  // "Aplicar a meses anteriores": the user picks the month the recurring expense really started
  const retroDate = (!isEditing && repeats && applyToPrevRecurring && retroYear !== '' && retroMonth !== ''
    && isMonthBefore(parseInt(retroYear), parseInt(retroMonth), startDate))
    ? buildRetroStartDate(parseInt(retroYear), parseInt(retroMonth), startDate)
    : null;
  const retroMonths = retroDate
    ? calcRetroMonths(retroDate, startDate, frequency, customFreqMonths,
        durationType === 'pagos' ? (parseInt(durationPayments) || undefined) : undefined)
    : [];
  const startYearNum = parseInt(startDate.substring(0, 4)) || new Date().getFullYear();
  const retroYearOptions = Array.from({ length: startYearNum - 1999 }, (_, i) => startYearNum - i);
  const enableRetro = () => {
    setApplyToPrevRecurring(true);
    if (retroYear === '' || retroMonth === '') {
      const d = defaultRetroMonth(startDate, frequency, customFreqMonths);
      setRetroYear(String(d.year));
      setRetroMonth(String(d.month));
    }
  };
  const freqStep = frequency === 'trimestral' ? 3 : frequency === 'anual' ? 12 : frequency === 'custom' ? (parseInt(customFreqMonths) || 1) : 1;
  const freqLabel = frequency === 'trimestral' ? 'trimestre' : frequency === 'anual' ? 'año' : frequency === 'custom' ? `${customFreqMonths || '?'} meses` : 'mes';
  const monthlyEquiv = amount && repeats && frequency !== 'mensual' ? (parseFloat(amount) / freqStep).toFixed(2) : null;

  // Fixed-duration expenses: if the first payment was earlier (e.g. an old mortgage), only count what's left
  const effectiveStart = (!isEditing && repeats && applyToPrevRecurring && retroDate) ? retroDate : startDate;
  const remainingPayments = repeats && durationType === 'pagos' && durationPayments
    ? calcRemainingPayments(durationPayments, retroDate ? '' : firstPaymentDate, effectiveStart, frequency, customFreqMonths)
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && repeats && applyToPrevRecurring && !retroDate) {
      alert('Elige un mes anterior a la fecha de inicio para aplicar el gasto a meses anteriores.');
      return;
    }
    if (remainingPayments !== null && remainingPayments < 1) {
      alert('Según la fecha del primer pago, ya se han hecho todos los pagos.');
      return;
    }
    let finalAttachmentUrl = attachmentUrl;
    if (attachmentFile) {
      setUploading(true);
      try {
        await supabase.storage.createBucket('gastos', { public: true });
        const safeName = attachmentFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${landlordEmail}/${propertyId}/${Date.now()}_${safeName}`;
        const { error: upErr } = await supabase.storage.from('gastos').upload(path, attachmentFile, { upsert: false });
        if (upErr) throw upErr;
        finalAttachmentUrl = supabase.storage.from('gastos').getPublicUrl(path).data.publicUrl;
      } catch (err) {
        alert(`Error subiendo el archivo: ${err.message}`);
        setUploading(false);
        return;
      }
      setUploading(false);
    }
    const catObj = EXPENSE_CATEGORIES.find(c => c.key === category);
    const parts = [catObj?.label || category];
    if (subcategory) parts.push(subcategory);
    if (description) parts.push(description);
    const expenseData = {
      name: parts.join(' — '),
      category,
      subcategory: subcategory || null,
      description,
      type: derivedType,
      frequency: !repeats ? 'unico' : frequency,
      custom_frequency_months: frequency === 'custom' && repeats ? (parseInt(customFreqMonths) || null) : null,
      amount: hasAmount ? parseFloat(amount) : null,
      duration_payments: remainingPayments,
      start_date: startDate,
      active: true,
      expense_percentage: expensePct ? parseFloat(expensePct) : null,
      attachment_url: finalAttachmentUrl || null,
    };
    if (isEditing) {
      onUpdate(initialExpense.id, expenseData);
    } else {
      const prevDates = applyToPrev ? Array.from(selectedMonths).sort().map(ym => `${ym}-01`) : [];
      const finalData = (repeats && applyToPrevRecurring && retroDate)
        ? { ...expenseData, start_date: retroDate }
        : expenseData;
      onAdd(finalData, prevDates.length > 0 ? prevDates : undefined);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{isEditing ? 'Editar gasto' : 'Añadir gasto'}</h2><button className="modal-close" onClick={onClose}>×</button></div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Categoría</label>
            <select value={category} onChange={e => handleCategoryChange(e.target.value)} required
              style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', background: 'white', color: category ? '#111' : '#aaa' }}>
              <option value="" disabled>Selecciona una categoría</option>
              {EXPENSE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          {category && (EXPENSE_CATEGORIES.find(c => c.key === category)?.subcategories?.length > 0) && (
            <div className="form-group">
              <label>Subcategoría</label>
              <div className="frequency-options subcategory-options">
                {EXPENSE_CATEGORIES.find(c => c.key === category).subcategories.map(s => (
                  <button key={s} type="button"
                    className={`frequency-option ${subcategory === s ? 'selected' : ''}`}
                    onClick={() => setSubcategory(s === subcategory ? '' : s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!(isPastMonth && !isEditing) && (
            <div className="form-group">
              <label>¿Se repite?</label>
              <div className="frequency-options">
                <button type="button" className={`frequency-option ${!repeats ? 'selected' : ''}`} onClick={() => setRepeats(false)}>No</button>
                <button type="button" className={`frequency-option ${repeats ? 'selected' : ''}`} onClick={() => setRepeats(true)}>Sí</button>
              </div>
            </div>
          )}
          {repeats && !(isPastMonth && !isEditing) && (
            <>
              <div className="form-group">
                <label>Frecuencia</label>
                <div className="frequency-options frequency-grid">
                  {['mensual', 'trimestral', 'anual', 'custom'].map(f => (
                    <button key={f} type="button"
                      className={`frequency-option ${frequency === f ? 'selected' : ''}`}
                      onClick={() => setFrequency(f)}>
                      {f === 'mensual' ? 'Mensual' : f === 'trimestral' ? 'Trimestral' : f === 'anual' ? 'Anual' : 'Otra'}
                    </button>
                  ))}
                </div>
                {frequency === 'custom' && (
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>Cada</span>
                    <input type="number" min="1" placeholder="2" value={customFreqMonths}
                      onChange={e => setCustomFreqMonths(e.target.value)} required
                      style={{ width: 70, padding: '8px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, textAlign: 'center' }} />
                    <span style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>meses</span>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Duración</label>
                <div className="frequency-options">
                  <button type="button" className={`frequency-option ${durationType === 'indefinido' ? 'selected' : ''}`} onClick={() => setDurationType('indefinido')}>Indefinido</button>
                  <button type="button" className={`frequency-option ${durationType === 'pagos' ? 'selected' : ''}`} onClick={() => setDurationType('pagos')}>Nº de pagos</button>
                </div>
                {durationType === 'pagos' && (
                  <div style={{ marginTop: 10 }}>
                    <input type="number" min="1" placeholder="Ej: 12" value={durationPayments}
                      onChange={e => setDurationPayments(e.target.value)} required
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }} />
                    {!applyToPrevRecurring && (
                      <>
                      <label style={{ marginTop: 14 }}>{category === 'hipoteca' ? 'Inicio de la hipoteca (opcional)' : 'Fecha del primer pago (opcional)'}</label>
                      <input type="date" value={firstPaymentDate} max={effectiveStart}
                        onChange={e => setFirstPaymentDate(e.target.value)} />
                      <p style={{ fontSize: 12, color: '#888', marginTop: 6, lineHeight: 1.4 }}>
                        Si empezaste a pagar antes, indica cuándo y solo se contarán los pagos que quedan.
                      </p>
                      </>
                    )}
                    {remainingPayments !== null && (
                      <p className="monthly-equivalent">
                        {remainingPayments < 1
                          ? 'Según esta fecha ya se han hecho todos los pagos'
                          : `${remainingPayments !== parseInt(durationPayments) ? `Quedan ${remainingPayments} de ${durationPayments} pagos · ` : ''}Último pago: ${calcLastPaymentLabel(remainingPayments, effectiveStart, frequency, customFreqMonths)}`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {repeats && !isEditing && (
            <div className="form-group">
              <label>¿Aplicar a meses anteriores?</label>
              <div className="frequency-options">
                <button type="button"
                  className={`frequency-option ${!applyToPrevRecurring ? 'selected' : ''}`}
                  onClick={() => setApplyToPrevRecurring(false)}>No</button>
                <button type="button"
                  className={`frequency-option ${applyToPrevRecurring ? 'selected' : ''}`}
                  onClick={enableRetro}>Sí</button>
              </div>
              {applyToPrevRecurring && (
                <div style={{ marginTop: 12 }}>
                  <label>Desde</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <select value={retroMonth} onChange={e => setRetroMonth(e.target.value)}
                      style={{ flex: 3, minWidth: 0, padding: '12px', borderRadius: 10, border: '1px solid #ddd', fontSize: 15, background: 'white' }}>
                      {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((label, idx) => (
                        <option key={idx} value={idx}
                          disabled={retroYear !== '' && !isMonthBefore(parseInt(retroYear), idx, startDate)}>{label}</option>
                      ))}
                    </select>
                    <select value={retroYear} onChange={e => setRetroYear(e.target.value)}
                      style={{ flex: 2, minWidth: 0, padding: '12px', borderRadius: 10, border: '1px solid #ddd', fontSize: 15, background: 'white' }}>
                      {retroYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  {!retroDate && (
                    <p style={{ fontSize: 12, color: '#C62828', marginTop: 6 }}>Elige un mes anterior a la fecha de inicio.</p>
                  )}
                  {retroDate && retroMonths.length > 0 && (
                    <p className="monthly-equivalent">
                      {retroMonths.length <= 6
                        ? `Se añadirá también en: ${retroMonths.join(', ')}`
                        : `Se añadirán ${retroMonths.length} pagos anteriores: ${retroMonths[0]} – ${retroMonths[retroMonths.length - 1]}`}
                    </p>
                  )}
                </div>
              )}
              {applyToPrevRecurring && hasExistingRecurring && (
                <p style={{ fontSize: 12, color: '#B26A00', marginTop: 6, lineHeight: 1.4 }}>
                  Ya tienes un gasto de esta categoría con la misma frecuencia. Si ya cubre esos meses, se duplicará.
                </p>
              )}
            </div>
          )}
          <div className="form-group">
            <label>Importe por {!repeats ? 'pago' : freqLabel} (€){repeats ? ' — opcional si varía' : ''}</label>
            <input type="number" placeholder={repeats ? '0.00 (deja en blanco si varía)' : '0.00'}
              value={amount} onChange={e => setAmount(e.target.value)}
              required={!repeats} step="0.01" min="0" />
            {monthlyEquiv && <p className="monthly-equivalent">Equivalente mensual: {monthlyEquiv} €/mes</p>}
            {repeats && !hasAmount && <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Introduces el importe cuando llega la factura.</p>}
          </div>
          <div className="form-group">
            <label>Fecha de inicio</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>% de titularidad</label>
            <input type="number" placeholder={`${defaultExpensePct ?? 100}`} value={expensePct}
              onChange={e => setExpensePct(e.target.value)} step="0.01" min="0" max="100" />
          </div>
          <div className="form-group">
            <label>Descripción (opcional)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="text" placeholder="Ej: recibo gas, fontanero..." value={description}
                onChange={e => setDescription(e.target.value)} style={{ flex: 1 }} />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                style={{ display: 'none' }}
                onChange={e => { setAttachmentFile(e.target.files[0] || null); }}
              />
              <button
                type="button"
                title="Adjuntar imagen o PDF"
                onClick={() => fileInputRef.current?.click()}
                style={{ background: attachmentFile ? '#E3F2FD' : 'none', border: '1px solid #ddd', borderRadius: 8, padding: '10px', cursor: 'pointer', color: attachmentFile ? '#1976D2' : '#aaa', lineHeight: 1, flexShrink: 0, display: 'flex', alignItems: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
              </button>
            </div>
            {attachmentFile && (
              <p style={{ fontSize: 11, color: '#555', marginTop: 4 }}>📄 {attachmentFile.name}</p>
            )}
            {!attachmentFile && attachmentUrl && (
              <p style={{ fontSize: 11, marginTop: 4 }}>
                <a href={attachmentUrl} target="_blank" rel="noreferrer" style={{ color: '#2196F3' }}>Ver archivo adjunto actual</a>
                <button type="button" onClick={() => setAttachmentUrl('')} style={{ marginLeft: 8, fontSize: 10, background: 'none', border: 'none', color: '#aaa', cursor: 'pointer' }}>Quitar</button>
              </p>
            )}
            {uploading && <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Subiendo archivo…</p>}
          </div>
          {isPastMonth && !isEditing && (
            <div className="form-group">
              <label>¿Aplicar a meses anteriores?</label>
              <div className="frequency-options" style={{ marginBottom: applyToPrev ? 10 : 0 }}>
                <button type="button" className={`frequency-option ${!applyToPrev ? 'selected' : ''}`} onClick={() => { setApplyToPrev(false); setSelectedMonths(new Set()); }}>No</button>
                <button type="button" className={`frequency-option ${applyToPrev ? 'selected' : ''}`} onClick={() => setApplyToPrev(true)}>Sí</button>
              </div>
              {applyToPrev && <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <button type="button"
                    onClick={() => setPrevPickerYear(y => y - 1)}
                    disabled={prevPickerYear <= 2020}
                    style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#555', padding: '2px 10px', lineHeight: 1 }}>
                    ‹
                  </button>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{prevPickerYear}</span>
                  <button type="button"
                    onClick={() => setPrevPickerYear(y => y + 1)}
                    disabled={prevPickerYear >= maxPrevYear}
                    style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#555', padding: '2px 10px', lineHeight: 1 }}>
                    ›
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'].map((label, m) => {
                    const key = `${prevPickerYear}-${String(m + 1).padStart(2, '0')}`;
                    const disabled = prevPickerYear > viewedYear
                      || (prevPickerYear === viewedYear && m >= viewedMonth)
                      || prevPickerYear < 2020;
                    const isSelected = selectedMonths.has(key);
                    return (
                      <button key={m} type="button" disabled={disabled}
                        onClick={() => {
                          setSelectedMonths(prev => {
                            const next = new Set(prev);
                            if (next.has(key)) next.delete(key); else next.add(key);
                            return next;
                          });
                        }}
                        style={{
                          padding: '8px 4px', borderRadius: 8,
                          border: isSelected ? '2px solid #111' : '1px solid #ddd',
                          background: isSelected ? '#111' : disabled ? '#f5f5f5' : 'white',
                          color: isSelected ? 'white' : disabled ? '#ccc' : '#333',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          fontSize: 13, fontWeight: isSelected ? 600 : 400,
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                {selectedMonths.size > 0 && (
                  <p style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                    {selectedMonths.size} {selectedMonths.size === 1 ? 'mes seleccionado' : 'meses seleccionados'}
                  </p>
                )}
              </div>}
            </div>
          )}
          <button type="submit" className="submit-button" disabled={uploading}>{isEditing ? 'Guardar cambios' : 'Añadir gasto'}</button>
        </form>
      </div>
    </div>
  );
}

function EditRoomModal({ room, onClose, onSave, onRemoveTenant }) {
  const [name, setName] = useState(room.name);
  const [price, setPrice] = useState(room.price.toString());

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ name, price: parseFloat(price) });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar habitación</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre de la habitación</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Precio mensual (€)</label>
            <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} required step="0.01" />
          </div>
          {room.tenant && (
            <div className="form-group">
              <label>Inquilino actual</label>
              <div className="current-tenant-info">
                <div>
                  <p className="tenant-name">{room.tenant.name}</p>
                  <p className="tenant-phone">{room.tenant.phone}</p>
                </div>
                <button type="button" className="delete-button" onClick={() => {
                  onRemoveTenant();
                  onClose();
                }}>
                  Quitar inquilino
                </button>
              </div>
            </div>
          )}
          <button type="submit" className="submit-button">Guardar cambios</button>
        </form>
      </div>
    </div>
  );
}

function EditTenantModal({ tenant, room, onClose, onSave, onRemove }) {
  const [name, setName] = useState(tenant.name);
  const [phone, setPhone] = useState(tenant.phone);
  const [startDay, setStartDay] = useState(room?.paymentConfig?.startDay?.toString() || '1');
  const [endDay, setEndDay] = useState(room?.paymentConfig?.endDay?.toString() || '5');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ name, phone }, { startDay: parseInt(startDay), endDay: parseInt(endDay), limitDay: parseInt(endDay) });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar inquilino</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre completo</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Teléfono</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Rango de días para pagar el alquiler</label>
            <div className="payment-range-inputs">
              <div className="payment-day-input">
                <label>Del día</label>
                <input type="number" min="1" max="28" value={startDay} onChange={(e) => setStartDay(e.target.value)} required />
              </div>
              <span className="range-separator">—</span>
              <div className="payment-day-input">
                <label>Al día</label>
                <input type="number" min="1" max="31" value={endDay} onChange={(e) => setEndDay(e.target.value)} required />
              </div>
            </div>
            <p className="payment-range-note">El inquilino deberá marcar el pago entre el día {startDay} y {endDay} de cada mes</p>
          </div>
          <div className="modal-buttons">
            <button type="submit" className="submit-button">Guardar cambios</button>
            <button type="button" className="delete-button" onClick={() => {
              if (window.confirm('¿Eliminar inquilino de esta habitación?')) onRemove();
            }}>Quitar inquilino</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmPaymentModal({ defaultAmount, tenantName, onClose, onConfirm }) {
  const [amount, setAmount] = useState(defaultAmount?.toString() || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(parseFloat(amount));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Confirmar pago</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {tenantName && (
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px', marginTop: '-8px' }}>
              Inquilino: <strong>{tenantName}</strong>
            </p>
          )}
          <div className="form-group">
            <label>Importe recibido (€)</label>
            <input
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              step="0.01"
              min="0"
              required
              autoFocus
            />
            {parseFloat(amount) !== defaultAmount && parseFloat(amount) > 0 && (
              <p className="payment-range-note" style={{ marginTop: '8px' }}>
                Precio base: {defaultAmount} € — estás registrando un importe diferente
              </p>
            )}
          </div>
          <button type="submit" className="submit-button">
            Confirmar pago
          </button>
        </form>
      </div>
    </div>
  );
}

export default RoomDetail;